import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Ban, Check, Loader2, Plus, Trash2, Users, X } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Chip, Empty, Err, Ok, Panel } from "@/components/community/kit";
import { day } from "@/lib/community";

const blank = {
  name: "", type: "community", city: "", state: "", membership: "approval",
  description: "", owner_email: "", election_frequency: "annual",
};

export const CommunityAdmin = () => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(blank);
  const [creating, setCreating] = useState(false);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get("/admin/communities");
      setData(res);
    } catch (e) { setError(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, key) => {
    setBusy(key); setError(""); setNotice("");
    try { await fn(); await load(); } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  if (!data) return <Loader2 className="mx-auto h-5 w-5 animate-spin text-gold" />;

  return (
    <div data-testid="admin-communities" className="space-y-5">
      <Err testId="admin-communities-error">{error}</Err>
      <Ok testId="admin-communities-notice">{notice}</Ok>

      {data.applications.filter((a) => a.status === "pending").length > 0 && (
        <Panel testId="community-applications" title="Drivers asking for a community">
          <ul className="space-y-3">
            {data.applications.filter((a) => a.status === "pending").map((a) => (
              <li key={a.id} data-testid={`app-row-${a.id}`}
                className="rounded-xl border border-ink-600 bg-ink-900/60 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-slate-100">{a.name}</p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {[a.city, a.state].filter(Boolean).join(", ")} · {a.applicant} ({a.email}) · {day(a.created_at)}
                    </p>
                    {a.note && <p className="mt-2 whitespace-pre-wrap text-xs text-slate-400">{a.note}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`app-approve-${a.id}`} size="sm"
                      onClick={() => run(async () => {
                        await api.post(`/admin/community-applications/${a.id}`,
                          { status: "approved", note: notes[a.id] || "" });
                        setNotice(`${a.name} is live — the applicant is its owner.`);
                      }, `ok-${a.id}`)} disabled={busy === `ok-${a.id}`}>
                      <Check className="mr-1.5 h-3.5 w-3.5" /> Approve
                    </Button>
                    <Button data-testid={`app-decline-${a.id}`} size="sm" variant="outline"
                      onClick={() => run(async () => {
                        await api.post(`/admin/community-applications/${a.id}`,
                          { status: "declined", note: notes[a.id] || "" });
                      }, `no-${a.id}`)} disabled={busy === `no-${a.id}`}>
                      <X className="mr-1.5 h-3.5 w-3.5" /> Decline
                    </Button>
                  </div>
                </div>
                <Input data-testid={`app-note-${a.id}`} className="mt-3"
                  placeholder="A note back to the applicant (optional)"
                  value={notes[a.id] || ""}
                  onChange={(e) => setNotes({ ...notes, [a.id]: e.target.value })} />
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel testId="community-create" title="Open a community"
        sub="Only you can create these. Name an owner by their LimoLinkX email, or leave it blank and appoint one later."
        action={
          <Button data-testid="community-create-toggle" size="sm" variant="outline"
            onClick={() => setCreating((v) => !v)}>
            {creating ? "Cancel" : <><Plus className="mr-1.5 h-3.5 w-3.5" /> New community</>}
          </Button>
        }>
        {creating && (
          <div className="space-y-3">
            <Input data-testid="create-name" placeholder="Houston Black Car Drivers"
              value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            <div className="grid gap-3 sm:grid-cols-3">
              <select data-testid="create-type" value={draft.type}
                onChange={(e) => setDraft({ ...draft, type: e.target.value })}
                className="h-10 rounded-md border border-ink-600 bg-ink-900 px-3 text-sm text-slate-200">
                {Object.entries(data.types).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <Input data-testid="create-city" placeholder="City" value={draft.city}
                onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
              <Input data-testid="create-state" placeholder="State" value={draft.state}
                onChange={(e) => setDraft({ ...draft, state: e.target.value })} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <select data-testid="create-membership" value={draft.membership}
                onChange={(e) => setDraft({ ...draft, membership: e.target.value })}
                className="h-10 rounded-md border border-ink-600 bg-ink-900 px-3 text-sm text-slate-200">
                {Object.entries(data.modes).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <Input data-testid="create-owner" type="email" placeholder="Owner's LimoLinkX email"
                value={draft.owner_email}
                onChange={(e) => setDraft({ ...draft, owner_email: e.target.value })} />
            </div>
            <Textarea data-testid="create-description" rows={3} placeholder="What this community is for"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            <Button data-testid="create-submit" disabled={draft.name.trim().length < 3 || busy === "create"}
              onClick={() => run(async () => {
                const payload = { ...draft };
                if (!payload.owner_email) delete payload.owner_email;
                const { data: res } = await api.post("/admin/communities", payload);
                setNotice(`${res.community.name} created — code ${res.community.code}.`);
                setDraft(blank);
                setCreating(false);
              }, "create")}>
              {busy === "create" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Create it
            </Button>
          </div>
        )}
      </Panel>

      {data.communities.length === 0 ? (
        <Empty testId="admin-communities-empty">No communities yet.</Empty>
      ) : (
        <Panel testId="admin-communities-list" title={`Communities · ${data.communities.length}`}>
          <ul className="divide-y divide-ink-600">
            {data.communities.map((c) => (
              <li key={c.id} data-testid={`admin-community-${c.id}`} className="py-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 text-sm text-slate-100">
                      {c.name}
                      <Chip>{c.code}</Chip>
                      {c.suspended && (
                        <Chip className="border-red-500/40 bg-red-500/10 text-red-300">suspended</Chip>
                      )}
                      {!c.listed && <Chip>unlisted</Chip>}
                      {c.payments_ready && (
                        <Chip className="border-emerald-500/40 bg-emerald-500/10 text-emerald-300">
                          dues live
                        </Chip>
                      )}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {c.type_label} · {[c.city, c.state].filter(Boolean).join(", ") || "—"} ·{" "}
                      <Users className="inline h-3 w-3" /> {c.counts?.members || 0} members
                      {c.counts?.pending ? ` · ${c.counts.pending} waiting` : ""}
                      {c.owners?.length ? ` · owner ${c.owners[0].name}` : " · no owner yet"}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/admin/community/${c.id}`} data-testid={`admin-community-open-${c.id}`}
                      className="rounded-md border border-ink-600 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-300 hover:border-gold/40 hover:text-gold">
                      Open
                    </Link>
                    <Button data-testid={`admin-community-suspend-${c.id}`} size="sm" variant="outline"
                      onClick={() => run(async () => {
                        await api.patch(`/admin/communities/${c.id}`, { suspended: !c.suspended });
                      }, `sus-${c.id}`)} disabled={busy === `sus-${c.id}`}>
                      <Ban className="mr-1.5 h-3.5 w-3.5" /> {c.suspended ? "Lift" : "Suspend"}
                    </Button>
                    <Button data-testid={`admin-community-delete-${c.id}`} size="sm" variant="ghost"
                      className="text-slate-400 hover:text-red-300"
                      onClick={() => run(async () => {
                        if (!window.confirm(`Delete ${c.name} and everything in it?`)) return;
                        await api.delete(`/admin/communities/${c.id}`);
                      }, `del-${c.id}`)} disabled={busy === `del-${c.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
};
